import React from 'react';
import { 
    Network, Building2, Copy, ShieldCheck, 
    LineChart, ArrowRight, MapPin, Globe, 
    CheckCircle2, Server
} from 'lucide-react';

const MultiBranchSetup = () => {
    // Setup Steps Data
    const steps = [
        {
            id: '01',
            icon: Building2,
            title: "Add a New Location",
            description: "Simply input the physical address, tax rates, and branch manager details. The system automatically creates a secure, isolated database partition for the new restaurant.",
            image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=1200&auto=format&fit=crop" // Restaurant facade/interior
        },
        {
            id: '02',
            icon: Copy,
            title: "1-Click Menu Clone",
            description: "Don't rebuild your menu from scratch. Instantly clone your master menu, recipes, and inventory items to the new branch. You can then tweak local pricing if needed.",
            image: "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?q=80&w=1200&auto=format&fit=crop" // Restaurant POS / Tablet interface
        },
        {
            id: '03',
            icon: ShieldCheck,
            title: "Lock Down HR & Roles",
            description: "Assign staff to the new location. Our geofencing engine instantly updates, ensuring employees can only clock in when they are physically inside the new building.",
            image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=1200&auto=format&fit=crop" // Security / tech team
        }
    ];

    return (
        <div className="min-h-screen bg-slate-950 font-sans selection:bg-blue-500/30 pb-24 overflow-hidden">
            
            {/* --- TOP HERO SECTION --- */}
            <div className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden border-b border-slate-800">
                {/* Background Image & Overlays */}
                <div 
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-30"
                    style={{ backgroundImage: "url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2000&auto=format&fit=crop')" }} // Global network/map vibe
                ></div>
                <div className="absolute inset-0 z-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent"></div>
                <div className="absolute inset-0 z-0 bg-gradient-to-b from-slate-950 via-transparent to-transparent"></div>

                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-md border border-slate-700 text-blue-400 text-xs font-bold uppercase tracking-widest mb-8 shadow-lg shadow-blue-500/10">
                        <Network size={14} className="text-blue-500" /> Global Expansion
                    </div>
                    
                    {/* Constrained Typography */}
                    <div className="max-w-4xl mx-auto">
                        <h1 className="text-4xl md:text-5xl lg:text-7xl font-black text-white tracking-tight leading-[1.1] mb-6">
                            Command your empire. <br className="hidden md:block" />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                                Expand without limits.
                            </span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-400 font-medium max-w-2xl mx-auto leading-relaxed">
                            Adding a new restaurant location shouldn't mean buying new software. Deploy new branches in seconds and manage them all from one centralized HQ dashboard.
                        </p>
                    </div>
                </div>
            </div>

            {/* --- CENTRAL COMMAND VISUALIZATION --- */}
            <div className="max-w-5xl mx-auto px-6 lg:px-8 -mt-12 relative z-20">
                <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 md:p-12 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-12 overflow-hidden relative">
                    
                    {/* Subtle glow behind the visual */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/20 blur-[100px] rounded-full pointer-events-none"></div>

                    <div className="w-full md:w-1/2 relative z-10">
                        <h3 className="text-2xl md:text-3xl font-black text-white mb-4">Centralized HQ Architecture</h3>
                        <p className="text-slate-400 mb-6 leading-relaxed">
                            Every branch operates independently to ensure zero lag during rush hour, but all data instantly syncs back to your Master Database for global reporting.
                        </p>
                        <div className="space-y-3">
                            {['Zero offline downtime', 'Unified raw inventory tracking', 'Cross-branch staff transfers'].map((item, i) => (
                                <div key={i} className="flex items-center gap-3 text-slate-300 font-medium text-sm">
                                    <CheckCircle2 size={16} className="text-blue-500" /> {item}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Abstract Network Graphic */}
                    <div className="w-full md:w-1/2 flex items-center justify-center relative z-10">
                        <div className="relative w-64 h-64">
                            {/* HQ Node */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center z-20 shadow-[0_0_30px_rgba(37,99,235,0.5)] border border-blue-400">
                                <Server size={32} className="text-white" />
                            </div>
                            
                            {/* Branch Nodes & Connecting Lines */}
                            {[
                                { x: '10%', y: '10%', delay: '0s' },
                                { x: '80%', y: '15%', delay: '0.2s' },
                                { x: '15%', y: '80%', delay: '0.4s' },
                                { x: '85%', y: '85%', delay: '0.6s' }
                            ].map((pos, i) => (
                                <div key={i}>
                                    {/* Line to Center */}
                                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" style={{ opacity: 0.3 }}>
                                        <line x1="50%" y1="50%" x2={pos.x} y2={pos.y} stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 4" className="animate-[dash_3s_linear_infinite]" />
                                    </svg>
                                    {/* Branch Dot */}
                                    <div 
                                        className="absolute w-12 h-12 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center z-10 shadow-lg"
                                        style={{ top: pos.y, left: pos.x, transform: 'translate(-50%, -50%)' }}
                                    >
                                        <MapPin size={18} className="text-blue-400" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>

            {/* --- THE SETUP PROCESS (Alternating Layout) --- */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32">
                <div className="text-center max-w-2xl mx-auto mb-20">
                    <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Deploy in minutes, not months.</h2>
                    <p className="text-slate-400 text-lg">The streamlined 3-step process to bring a new restaurant location online.</p>
                </div>

                <div className="space-y-24 md:space-y-32">
                    {steps.map((step, idx) => {
                        const isEven = idx % 2 === 0;
                        return (
                            <div key={step.id} className={`flex flex-col gap-10 md:gap-16 items-center ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                                
                                {/* Image Side */}
                                <div className="w-full md:w-1/2">
                                    <div className="relative group rounded-[2rem] overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl p-2">
                                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                        <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
                                            <div 
                                                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                                                style={{ backgroundImage: `url('${step.image}')` }}
                                            ></div>
                                            {/* Soft inner shadow for depth */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent"></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Text Side (Constrained) */}
                                <div className="w-full md:w-1/2 max-w-lg">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="text-5xl font-black text-slate-800 select-none">{step.id}</div>
                                        <div className="w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shadow-inner">
                                            <step.icon size={24} />
                                        </div>
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-black text-white mb-4 tracking-tight">
                                        {step.title}
                                    </h3>
                                    <p className="text-slate-400 text-lg leading-relaxed mb-8">
                                        {step.description}
                                    </p>
                                    
                                    <button className="text-blue-400 font-bold hover:text-blue-300 flex items-center gap-2 group transition-colors">
                                        View documentation 
                                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>
            </div>

            {/* --- GLOBAL REPORTING BANNER --- */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32">
                <div className="bg-gradient-to-br from-blue-900/40 to-slate-900 border border-blue-500/20 rounded-[2.5rem] p-10 md:p-16 text-center relative overflow-hidden shadow-2xl">
                    {/* Decor elements */}
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                        <Globe size={120} className="text-blue-400" />
                    </div>
                    
                    <div className="max-w-2xl mx-auto relative z-10">
                        <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.3)]">
                            <LineChart size={32} className="text-blue-400" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tight">Enterprise Reporting</h2>
                        <p className="text-slate-300 text-lg mb-10 leading-relaxed">
                            Once your branches are live, jump to the Executive Dashboard to view comparative analytics. See which location drives the highest net profit, in real-time.
                        </p>
                        <button className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-900/20 hover:-translate-y-1 w-full sm:w-auto mx-auto">
                            Explore Analytics Dashboard
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes dash {
                    to { stroke-dashoffset: -8; }
                }
            `}</style>

        </div>
    );
};

export default MultiBranchSetup;